/**
 * Path → module or redirect (#92). Pure; every redirect target resolves to
 * an enabled module by construction, so the shell needs no loop cap.
 */
import type { AppModule, RouteResolution } from './module.types';

export const HOME_ROUTE = '/';

/** null: route matches exactly; string: the subroute; undefined: no match. */
function matchRoute(route: string, path: string): string | null | undefined {
  if (path === route) return null;
  if (route !== HOME_ROUTE && path.startsWith(`${route}/`)) {
    return path.slice(route.length + 1);
  }
  return undefined;
}

/** A module's default path: its first subroute, or its bare route. */
export function defaultPath(module: AppModule): string {
  const [first] = module.subroutes ?? [];
  return first ? `${module.route}/${first}` : module.route;
}

export function resolveRoute(
  path: string | null,
  modules: readonly AppModule[]
): RouteResolution {
  if (path === null) return { kind: 'redirect', to: HOME_ROUTE };
  const split = path.indexOf('?');
  if (split !== -1) {
    const bare = resolveRoute(path.slice(0, split), modules);
    if (bare.kind !== 'module') return bare;
    return {
      ...bare,
      path,
      params: {
        ...bare.params,
        query: Object.fromEntries(new URLSearchParams(path.slice(split + 1))),
      },
    };
  }

  for (const module of modules) {
    const subroute = matchRoute(module.route, path);
    if (subroute === undefined) continue;

    if (!module.enabled) return { kind: 'redirect', to: HOME_ROUTE };

    const [defaultSubroute] = module.subroutes ?? [];
    if (defaultSubroute === undefined) {
      if (subroute === null) {
        return { kind: 'module', module, path, params: { subroute: null } };
      }
      return module.acceptsSubroute?.(subroute)
        ? { kind: 'module', module, path, params: { subroute } }
        : { kind: 'redirect', to: module.route };
    }

    if (subroute === null || !module.subroutes?.includes(subroute)) {
      return { kind: 'redirect', to: `${module.route}/${defaultSubroute}` };
    }
    return { kind: 'module', module, path, params: { subroute } };
  }

  return { kind: 'redirect', to: HOME_ROUTE };
}

/** Whether a module's route should show as active for the given path. */
export function isActiveRoute(route: string, path: string): boolean {
  const bare = path.split('?')[0] ?? path;
  if (route === HOME_ROUTE) return bare === HOME_ROUTE;
  return bare === route || bare.startsWith(`${route}/`);
}
